// useCalendarView — state и производные календарного вида (U-39 V13, M3):
// anchorDate (единственный источник времени) + viewMode; производные — месяц для MonthGrid,
// диапазон запроса с ЛОКАЛЬНЫМИ границами дня/недели (M1) и видозависимый заголовок.
// Чистые функции stepFor/computeRange/titleFor — по §12.3 Tech-Story; B3: все
// format(...) — только с { locale: ru } (канон проекта: CalendarPage.tsx:152, MonthGrid.tsx:99).
// Стратегия по enum: month/week/day — один выбор, без switch-каскада в рендере.
import { useCallback, useMemo, useState } from 'react';
import {
  parseISO,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  addDays,
  format,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import type { DateRangeValue } from '@/pages/calendar/components/DateRangeFilter';

/** Вид календаря (единственный enum — расширение = новое значение + компонент). */
export type CalendarViewMode = 'month' | 'week' | 'day';

/** Диапазон запроса календаря: локальные границы (M1) + локальные дни-колонки. */
export interface CalendarViewRange {
  from: string;
  to: string;
  days: Date[];
}

/** Шаг навигации по виду (M3): month — ±1 месяц, week — ±7 дней, day — ±1 день. */
export function stepFor(view: CalendarViewMode): { months: number; days: number } {
  switch (view) {
    case 'month':
      return { months: 1, days: 0 };
    case 'week':
      return { months: 0, days: 7 };
    case 'day':
      return { months: 0, days: 1 };
  }
}

/**
 * Единая точка расчёта диапазона (M1): локальные границы дня/недели; месяц — календарные from/to.
 * При активном dateRange (override) — приоритет ему, границы ЛОКАЛЬНЫЕ
 * (startOfDay/endOfDay → .toISOString(), не `T00:00:00Z`); колонки недели = дни интервала.
 * W2-6: from > to — нормализация swap (BE не валидирует обратный диапазон).
 */
export function computeRange(
  anchorDate: Date,
  viewMode: CalendarViewMode,
  dateRange: DateRangeValue,
): CalendarViewRange {
  if (Boolean(dateRange.from) && Boolean(dateRange.to)) {
    let fromDay = parseISO(dateRange.from as string);
    let toDay = parseISO(dateRange.to as string);
    if (fromDay.getTime() > toDay.getTime()) [fromDay, toDay] = [toDay, fromDay];
    return {
      from: startOfDay(fromDay).toISOString(),
      to: endOfDay(toDay).toISOString(),
      days: eachDayOfInterval({ start: fromDay, end: toDay }),
    };
  }
  if (viewMode === 'week') {
    const start = startOfWeek(anchorDate, { weekStartsOn: 1 });
    const end = endOfWeek(anchorDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
    return { from: startOfDay(start).toISOString(), to: endOfDay(end).toISOString(), days };
  }
  if (viewMode === 'day') {
    const day = startOfDay(anchorDate);
    return { from: day.toISOString(), to: endOfDay(day).toISOString(), days: [day] };
  }
  const monthStart = startOfMonth(anchorDate);
  return { from: monthStart.toISOString(), to: endOfMonth(monthStart).toISOString(), days: [] };
}

/** Заголовок вида (M3, B3): month — «LLLL yyyy»; week — «d MMM – d MMM yyyy»; day — «EEEE, d MMMM». */
export function titleFor(anchorDate: Date, viewMode: CalendarViewMode, days: Date[]): string {
  if (viewMode === 'week') {
    return `${format(days[0] as Date, 'd MMM', { locale: ru })} – ${format(
      days[days.length - 1] as Date,
      'd MMM yyyy',
      { locale: ru },
    )}`;
  }
  if (viewMode === 'day') {
    return format(days[0] as Date, 'EEEE, d MMMM', { locale: ru });
  }
  return format(anchorDate, 'LLLL yyyy', { locale: ru });
}

/** Хук вида календаря: viewMode + anchorDate + производные (month/range/title) и навигация (M3). */
export function useCalendarView(dateRange: DateRangeValue): {
  viewMode: CalendarViewMode;
  setViewMode: (view: CalendarViewMode) => void;
  anchorDate: Date;
  setAnchorDate: (date: Date) => void;
  month: Date;
  range: CalendarViewRange;
  title: string;
  prev: () => void;
  next: () => void;
  today: () => void;
} {
  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());

  const step = stepFor(viewMode);
  const shiftAnchor = useCallback(
    (direction: -1 | 1) => {
      setAnchorDate((a) => addDays(addMonths(a, direction * step.months), direction * step.days));
    },
    [step.months, step.days],
  );

  const month = useMemo(() => startOfMonth(anchorDate), [anchorDate]);
  const range = useMemo(
    () => computeRange(anchorDate, viewMode, dateRange),
    // dateRange из useState-объекта: идентичность меняется только при изменении фильтра.
    [anchorDate, viewMode, dateRange],
  );
  const title = useMemo(() => titleFor(anchorDate, viewMode, range.days), [anchorDate, viewMode, range]);

  const prev = useCallback(() => shiftAnchor(-1), [shiftAnchor]);
  const next = useCallback(() => shiftAnchor(1), [shiftAnchor]);
  const today = useCallback(() => setAnchorDate(new Date()), []);

  return { viewMode, setViewMode, anchorDate, setAnchorDate, month, range, title, prev, next, today };
}
